import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import ModalContentBase from '../modal/hooks/modal_content_base';
import ActionModal from './action_modal';

const notePlaceholder = 'https://note.com/tunagate_pr/n/nabcdef123456';

// 生キー / 記事URL / 埋め込みURL / 貼り付けiframeコード のいずれからも note 記事キー（nXXXX）を取り出す。
// キー直後が URL区切り・属性の引用符・空白・文字列末尾のいずれかであることも検証し、
// 不正な入力（例: 大文字混じりの nabcDEF）を途中で切り詰めて別のキーとして拾わないようにする。
// サーバ側（content.rb の normalize_note_key_if_needed）にも同じ境界を揃えること。
const extractNoteKey = (value) => {
  const v = (value || '').trim();
  const matched =
    v.match(/notes\/(n[0-9a-z]+)(?=[/"'\s?#]|$)/) ||
    v.match(/\/n\/(n[0-9a-z]+)(?=[/"'\s?#]|$)/);
  if (matched) return matched[1];
  if (/^n[0-9a-z]+$/.test(v)) return v;
  return '';
};

const SectionNote = ({
  index,
  content,
  userAgent,
  updateContent,
  isSelected,
  setSelectedIndex,
  deleteContent,
  hideNextButtonFunc,
  displayNextButtonFunc,
}) => {
  const [bodyInput, setBodyInput] = useState('');
  const [isValidKey, setIsValidKey] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    const element = textareaRef.current;

    if (isSelected && element) {
      element.focus();
      element.setSelectionRange(element.value.length, element.value.length);
      element.scrollTop = element.scrollHeight;
    }
  }, [isSelected]);

  // note公式の埋め込みは iframe + embed.js が1セット（note ヘルプセンターの仕様）。
  // section_instagram.js と同じ手法でプレビュー表示中だけスクリプトを読み込む。
  useEffect(() => {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://note.com/scripts/embed.js';
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [content.body, isSelected]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // 入力中は表示用の状態だけ更新する。サーバへの保存（updateContent）は
  // 「適用」を押した時（applyNoteKey）にだけ行う。1文字ごとに保存すると、
  // 入力途中のゴミ文字列が保存されたり、非同期リクエストの順序不整合で
  // 新しい入力が古い入力に上書きされたりするため。
  const onChange = (e) => {
    const value = e.target.value;
    setBodyInput(value);

    if (value) {
      hideNextButtonFunc && hideNextButtonFunc();
    } else {
      displayNextButtonFunc && displayNextButtonFunc();
    }
  };

  // 「適用」クリック時に、その時点の入力から改めてキーを計算して保存する。
  // 古い state を見て判定する（stale closure）ことがないよう、判定と保存を同じ場所で行う。
  const applyNoteKey = () => {
    const key = extractNoteKey(bodyInput);
    if (key.length === 0) {
      setIsValidKey(false);
      return;
    }
    updateContent({ ...content, body: key });
    setSelectedIndex((prev) => (prev == index ? null : prev));
  };

  useEffect(() => {
    setIsValidKey(bodyInput === '' || extractNoteKey(bodyInput).length > 0);
  }, [bodyInput]);

  return (
    <Fragment>
      {isSelected || !isValidKey ? (
        <div className="d-flex mt-2" style={{ minWidth: '75%' }}>
          <div className="mt-2 pt-1" style={{ width: '90%' }}>
            {isValidKey ? (
              <p className="text-danger text-fz-small mt-2_5"></p>
            ) : (
              <p className="text-danger text-fz-small mb-0">
                書式が正しくありません（例：{notePlaceholder}）
              </p>
            )}
            <div className="m-0 d-flex">
              <textarea
                id="body"
                ref={textareaRef}
                className="textarea-no-border bg-white mt-0"
                style={{ fontSize: '16px' }}
                value={bodyInput}
                onChange={onChange}
                onFocus={() => setSelectedIndex(index)}
                placeholder={notePlaceholder}
              />
            </div>
          </div>
          {userAgent === 'pc' ? (
            <div className="d-flex justify-content-center align-items-center">
              <button
                className="btn btn-primary--grad ml-2 text-fw-bold text-white"
                onClick={applyNoteKey}
                style={{ width: '75px' }}
              >
                適用
              </button>
              <button
                className="btn btn-primary--grad ml-2 text-fw-bold text-white"
                onClick={() => {
                  setSelectedIndex((prev) => (prev == index ? null : prev));
                  deleteContent();
                }}
                style={{ width: '75px' }}
              >
                削除
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <button
                className="btn btn-primary--grad ml-2 text-fw-bold text-white"
                style={{ width: '20vw', maxWidth: '75px' }}
                onClick={applyNoteKey}
              >
                適用
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="max-w-100 cursor-pointer">
          <div
            className="w-100 h-100 position-absolute"
            onClick={() => setIsModalOpen(true)}
            style={{ zIndex: 100 }}
          />
          <div className="mt-3 mb-2">
            <iframe
              className="note-embed"
              src={`https://note.com/embed/notes/${content.body}`}
              height="460"
              style={{
                border: 0,
                display: 'block',
                maxWidth: '99%',
                width: '494px',
                margin: '10px auto',
              }}
            />
          </div>
        </div>
      )}
      {isModalOpen && (
        <ModalContentBase
          closeModal={() => closeModal()}
          modalContent={
            <ActionModal
              closeModal={closeModal}
              deleteContent={() => {
                closeModal();
                deleteContent();
              }}
              onEdit={() => {
                closeModal();
                setSelectedIndex(index);
                setBodyInput(content.body);
              }}
            />
          }
        />
      )}
      <div className="flex-grow-1" onClick={() => setIsModalOpen(true)} />
    </Fragment>
  );
};

export default SectionNote;
